import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { siteTemplatesApi } from '../api/client';
import type { SiteTemplate } from '../schemas/validation';
import { cn } from '../utils/cn';
import {
  normalizeComponents,
  processLayoutComponents,
  getTemplateThemeInfo,
} from '../utils/templateUtils';
import { ThemeProvider } from '../contexts/ThemeContext';
import { CustomColorsProvider } from '../contexts/CustomColorsContext';
import { ComponentRenderer } from './ComponentRenderer';

interface TemplatePreviewModalProps {
  template: SiteTemplate;
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: () => void;
  useMockData?: boolean;
}

interface PreviewLayout {
  id: string;
  name: string;
  components: any[] | null;
  isDefault: boolean;
}

interface PreviewData {
  template: {
    id: string;
    name: string;
    theme: string;
  };
  page: {
    index: number;
    title: string;
    slug: string;
    components: any[];
    showLayout?: boolean;
  };
  layout?: PreviewLayout | null;
  totalPages: number;
  totalLayouts?: number;
  allPages: Array<{ index: number; title: string; slug: string }>;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  isOpen,
  onClose,
  onSelectTemplate,
  useMockData = false,
}) => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Extract theme and brand package info for wrapping components with providers
  const themeInfo = useMemo(() => getTemplateThemeInfo(template), [template]);

  useEffect(() => {
    if (isOpen && template) {
      loadPreview(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, template?.id]);

  const loadPreview = async (pageIndex: number) => {
    try {
      setIsLoading(true);
      setError(null);

      if (useMockData) {
        // Generate mock preview data from template
        // Cast to any to handle dynamic siteData structure from templates
        const siteData = template.siteData as any;
        const pages = siteData?.pages || [];
        const layouts = siteData?.layouts || [];
        const page = pages[pageIndex] || pages[0];

        // Find layout for this page
        let pageLayout = null;
        if (page?.showLayout !== false) {
          if (page?.layoutId) {
            pageLayout = layouts.find((l: any) => l.id === page.layoutId);
          }
          if (!pageLayout) {
            pageLayout = layouts.find((l: any) => l.isDefault);
          }
        }

        setPreviewData({
          template: {
            id: template.id,
            name: template.name,
            theme: siteData?.theme || 'standard',
          },
          page: {
            index: pageIndex,
            title: page?.title || 'Page',
            slug: page?.slug || 'page',
            components: normalizeComponents(page?.components),
            showLayout: page?.showLayout !== false,
          },
          layout: pageLayout
            ? {
                id: pageLayout.id,
                name: pageLayout.name,
                components: normalizeComponents(pageLayout.components),
                isDefault: pageLayout.isDefault,
              }
            : null,
          totalPages: pages.length,
          totalLayouts: layouts.length,
          allPages: pages.map((p: any, idx: number) => ({
            index: idx,
            title: p.title,
            slug: p.slug,
          })),
        });
        setCurrentPageIndex(pageIndex);
      } else {
        const response = await siteTemplatesApi.previewTemplate(template.id, pageIndex);
        // Normalize components from API response as well
        // Cast response to any to handle the layout property that may not be in older cached types
        const apiResponse = response as any;
        setPreviewData({
          ...response,
          page: {
            ...response.page,
            components: normalizeComponents(response.page.components),
          },
          layout: apiResponse.layout
            ? {
                ...apiResponse.layout,
                components: normalizeComponents(apiResponse.layout.components),
              }
            : null,
        });
        setCurrentPageIndex(pageIndex);
      }
    } catch (err: any) {
      console.error('Failed to load template preview:', err);
      setError(err.message || 'Failed to load preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousPage = () => {
    if (previewData && currentPageIndex > 0) {
      loadPreview(currentPageIndex - 1);
    }
  };

  const handleNextPage = () => {
    if (previewData && currentPageIndex < previewData.totalPages - 1) {
      loadPreview(currentPageIndex + 1);
    }
  };

  const handlePageSelect = (pageIndex: number) => {
    loadPreview(pageIndex);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4',
        isOpen ? 'animate-fade-in' : ''
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={cn(
          'bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col',
          isOpen ? 'animate-slide-up' : ''
        )}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">Preview: {template.name}</h2>
            {useMockData && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                Demo Mode
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className={cn(
              'p-2 text-gray-400 hover:text-gray-600 rounded-lg',
              'hover:bg-gray-100 transition-all duration-200'
            )}
          >
            <X size={20} />
          </button>
        </div>

        {/* Page Navigation */}
        {previewData && previewData.totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2 p-3 bg-gray-50 border-b border-gray-200">
            <button
              onClick={handlePreviousPage}
              disabled={currentPageIndex === 0}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                currentPageIndex === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-200'
              )}
            >
              <ChevronLeft size={20} />
            </button>

            <div className="flex items-center space-x-1">
              {previewData.allPages.map((page, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePageSelect(idx)}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded-md transition-colors',
                    idx === currentPageIndex
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-gray-200'
                  )}
                  title={page.title}
                >
                  {page.title.length > 12 ? `${page.title.slice(0, 12)}...` : page.title}
                </button>
              ))}
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPageIndex === previewData.totalPages - 1}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                currentPageIndex === previewData.totalPages - 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-200'
              )}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}

        {/* Preview Content */}
        <div className="flex-1 overflow-auto bg-gray-100">
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Loader2 className="h-10 w-10 text-primary-600 animate-spin mx-auto" />
                <p className="mt-4 text-gray-600">Loading preview...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
                <p className="mt-4 text-gray-900 font-medium">Failed to load preview</p>
                <p className="mt-2 text-sm text-gray-600">{error}</p>
                <button onClick={() => loadPreview(currentPageIndex)} className="mt-4 btn-primary">
                  Retry
                </button>
              </div>
            </div>
          ) : previewData ? (
            (() => {
              const { headerComponents, footerComponents } = processLayoutComponents(
                previewData.layout?.components
              );
              const hasPageComponents =
                Array.isArray(previewData.page.components) &&
                previewData.page.components.length > 0;
              const hasLayoutComponents =
                headerComponents.length > 0 || footerComponents.length > 0;

              if (!hasPageComponents && !hasLayoutComponents) {
                return (
                  <div className="flex items-center justify-center h-96">
                    <div className="text-center text-gray-500">
                      <p className="text-lg">No components to preview</p>
                      <p className="text-sm mt-2">
                        {useMockData
                          ? 'This demo template has placeholder content only'
                          : 'This page has no components yet'}
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <div className="min-h-[400px] bg-white">
                  <ThemeProvider theme={themeInfo.themeName}>
                    <CustomColorsProvider
                      customColors={themeInfo.customColors}
                      fonts={themeInfo.fonts}
                    >
                      {/* Disable all clicks/interactions within the preview */}
                      <div className="component-list pointer-events-none select-none">
                        {/* Layout Header Components */}
                        {headerComponents.length > 0 && (
                          <div className="layout-header-components">
                            {headerComponents
                              .filter((c: any) => c.visible !== false)
                              .map((component: any) => (
                                <ComponentRenderer
                                  key={component.id}
                                  component={component}
                                  isEditing={false}
                                />
                              ))}
                          </div>
                        )}

                        {/* Page Components */}
                        {hasPageComponents &&
                          [...previewData.page.components]
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((component: any) => (
                              <ComponentRenderer
                                key={component.id}
                                component={component}
                                isEditing={false}
                              />
                            ))}

                        {/* Layout Footer Components */}
                        {footerComponents.length > 0 && (
                          <div className="layout-footer-components">
                            {footerComponents
                              .filter((c: any) => c.visible !== false)
                              .map((component: any) => (
                                <ComponentRenderer
                                  key={component.id}
                                  component={component}
                                  isEditing={false}
                                />
                              ))}
                          </div>
                        )}
                      </div>
                    </CustomColorsProvider>
                  </ThemeProvider>
                </div>
              );
            })()
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center text-gray-500">
                <p className="text-lg">No components to preview</p>
                <p className="text-sm mt-2">
                  {useMockData
                    ? 'This demo template has placeholder content only'
                    : 'This page has no components yet'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-white">
          <div className="text-sm text-gray-500">
            {previewData && (
              <>
                Page {currentPageIndex + 1} of {previewData.totalPages}
                {previewData.page && ` - ${previewData.page.title}`}
              </>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button onClick={onSelectTemplate} className="btn-primary">
              Use This Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
